/**
 * Farmer dashboard (web) — a G1 farmer's read-only roll-up of their OWN activity.
 *
 * Web twin of apps/mobile/app/my-harvests/insights.tsx, upgraded from a single
 * harvest-summary card into a small DASHBOARD that pulls together this farmer's
 * activity. Scopes to the signed-in user: every endpoint here is owner-scoped (the
 * bearer the fseAxios interceptor attaches identifies the caller), so there is no
 * user-id param. Read-only — a farmer who later joined a coop can still view their
 * past G1 figures (no independence gate on this route).
 *
 * FOUR independent data sources, each in its OWN query so one failing source never
 * blanks the page (each renders its own loading / error+retry / empty state):
 *   1. GET /harvests/summary  → the existing harvest-summary aggregate (KEPT in
 *      full: headline kg, by-status, by-grade, realised earnings). Numeric fields
 *      ship as STRINGS (Postgres numeric, precision-preserving) → grouped verbatim.
 *   2. GET /incentives/me/balance → { balance } integer points; a card + a hint
 *      linking to the Rewards page. Client: src/api/incentives.ts.
 *   3. GET /harvests/earnings → this-month + lifetime settled-sales rollup
 *      (PLAIN ZAR decimal strings, NOT micro-ZARP). Client: src/api/earnings.ts.
 *   4. GET /harvests/trend?months=6 → per-month kg + sales + harvest-count,
 *      oldest→newest, gap-filled (micro-ZARP for sales). Client: src/api/harvests.ts.
 *      Rendered as a Sparkline of monthly kg + a MarketStatBar headline, reusing
 *      the dependency-free chart pattern from _app.price-dashboard.tsx.
 *
 * MONEY: summary/earnings amounts are PLAIN ZAR decimal strings (display via
 * groupDigits / Number → <Money>, never re-multiplied); the trend's
 * salesAmountMicro is micro-ZARP → microToZarp() for display only. All copy via
 * t('insights.*').
 *
 * Summary contract verified against src/api/generated/models/harvestSummaryDto.ts
 * (validate-API-before-smoke rule):
 *   { totalHarvests, byStatus{open,locked,sold,settled,cancelled}, totalKg,
 *     perGradeKg{A,B,C}, salesCount, totalSalesAmount, avgPricePerKg, saleCurrency }
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Icon,
  ListItem,
  MarketStatBar,
  mdSysColor,
  Money,
  Sparkline,
  Stack,
  Text,
  useColorMode,
} from '@fse/ui';
import { Link } from '@tanstack/react-router';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError } from '../api/client';
import { getMyEarnings, type HarvestEarnings } from '../api/earnings';
import { harvestsControllerSummary } from '../api/generated/harvests/harvests';
import {
  getMyHarvestTrend,
  type HarvestTrendList,
  type HarvestTrendPoint,
} from '../api/harvests';
import { getIncentiveBalance, type IncentiveBalance } from '../api/incentives';
import { microToZarp } from '../api/market';
import { groupDigits } from '../lib/format';

const TREND_MONTHS = 6;

/** A label/value row rendered as a kit `ListItem`: muted label as the title,
 *  the value right-aligned + emphasised as a trailing `Text`. `noDivider`
 *  drops the trailing rule on the last row of a card. */
function DetailRow({
  label,
  value,
  leadingIcon,
  noDivider = false,
}: {
  label: string;
  value: ReactNode;
  leadingIcon?: string;
  noDivider?: boolean;
}) {
  return (
    <ListItem
      leadingIcon={leadingIcon}
      title={
        <Text variant="body-medium" color="text.secondary">
          {label}
        </Text>
      }
      trailing={
        <Box sx={{ textAlign: 'right' }}>
          <Text variant="body-medium" color="text.primary">
            <Box component="span" sx={{ fontWeight: 600 }}>
              {value}
            </Box>
          </Text>
        </Box>
      }
      noDivider={noDivider}
    />
  );
}

/** A bordered, rounded container wrapping a card's breakdown rows so the
 *  ListItem dividers read as a single grouped table (mirrors _app.my-farm.tsx). */
function RowGroup({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
}

/** Format a 'YYYY-MM' month key to a short label, e.g. "Jun". Falls back to the
 *  raw key if it can't be parsed (defensive; the contract guarantees 'YYYY-MM'). */
function formatMonthShort(monthKey: string): string {
  const [y, m] = monthKey.split('-');
  const year = Number(y);
  const month = Number(m);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
    return monthKey;
  }
  // Day 1 avoids any DST/timezone month-boundary drift.
  const d = new Date(year, month - 1, 1);
  return d.toLocaleDateString('en-ZA', { month: 'short' });
}

export function InsightsScreen() {
  const { t } = useTranslation();

  // 1. Harvest summary — the existing owner-scoped aggregate. This is the page's
  //    primary content; the headline/empty/error states below key off it.
  const summaryQuery = useQuery({
    queryKey: ['harvest-summary'],
    queryFn: ({ signal }) => harvestsControllerSummary(signal),
  });

  // 2. Incentive points balance — soft/secondary. Fails open into a quiet inline
  //    note (retry: false) so a points blip never disrupts the dashboard. Shares
  //    the ['incentives-balance'] key with the Rewards screen, so no double-fetch.
  const balanceQuery = useQuery({
    queryKey: ['incentives-balance'],
    queryFn: ({ signal }) => getIncentiveBalance(signal),
    retry: false,
  });

  // 3. Earnings rollup — this-month + lifetime settled sales. Soft; fails open.
  //    Shares the ['harvests-earnings'] key with the farmer Home hero.
  const earningsQuery = useQuery({
    queryKey: ['harvests-earnings'],
    queryFn: ({ signal }) => getMyEarnings(signal),
    retry: false,
  });

  // 4. Monthly trend — the NEW gap-filled per-month rollup. Soft; fails open.
  const trendQuery = useQuery({
    queryKey: ['harvest-trend', TREND_MONTHS],
    queryFn: ({ signal }) => getMyHarvestTrend({ months: TREND_MONTHS }, signal),
    retry: false,
  });

  const summary = summaryQuery.data;

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Box>
        <Text variant="headline-small" as="h2">
          {t('insights.title')}
        </Text>
        <Box sx={{ mt: 0.5 }}>
          <Text variant="body-medium" color="text.secondary">
            {t('insights.subtitle')}
          </Text>
        </Box>
      </Box>

      {/* ---- Source 2: incentive points (soft, independent of summary) ---- */}
      <PointsCard query={balanceQuery} t={t} />

      {/* ---- Source 3: earnings rollup (soft, independent of summary) ---- */}
      <EarningsCard query={earningsQuery} t={t} />

      {/* ---- Source 4: monthly trend chart (soft, independent of summary) ---- */}
      <TrendCard query={trendQuery} t={t} />

      {/* ---- Source 1: the harvest summary (primary; KEPT in full) ---- */}
      {summaryQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : summaryQuery.isError || !summary ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => summaryQuery.refetch()}>
              {t('insights.retry')}
            </Button>
          }
        >
          {t('insights.loadError', {
            error:
              summaryQuery.error instanceof FseApiError
                ? summaryQuery.error.message
                : String(summaryQuery.error),
          })}
        </Alert>
      ) : summary.totalHarvests === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={1}>
              <Text variant="title-medium" as="h3">
                {t('insights.empty.title')}
              </Text>
              <Text variant="body-medium" color="text.secondary">
                {t('insights.empty.body')}
              </Text>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Headline — total harvests + total kg */}
          <Card>
            <CardContent>
              <Stack spacing={0.5} sx={{ alignItems: 'center', textAlign: 'center', py: 1 }}>
                <Text variant="label-medium" color="text.secondary">
                  {t('insights.totalHarvests')}
                </Text>
                <Text variant="display-small" color="primary.main">
                  {String(summary.totalHarvests)}
                </Text>
                <Text variant="body-medium" color="text.secondary">
                  {t('insights.totalKg', { kg: groupDigits(summary.totalKg) })}
                </Text>
              </Stack>
            </CardContent>
          </Card>

          {/* By-status breakdown */}
          <Card>
            <CardHeader title={t('insights.byStatus.title')} />
            <CardContent>
              <RowGroup>
                <DetailRow
                  leadingIcon="hourglass_empty"
                  label={t('insights.byStatus.open')}
                  value={String(summary.byStatus.open)}
                />
                <DetailRow
                  leadingIcon="shield"
                  label={t('insights.byStatus.locked')}
                  value={String(summary.byStatus.locked)}
                />
                <DetailRow
                  leadingIcon="sell"
                  label={t('insights.byStatus.sold')}
                  value={String(summary.byStatus.sold)}
                />
                <DetailRow
                  leadingIcon="check_circle"
                  label={t('insights.byStatus.settled')}
                  value={String(summary.byStatus.settled)}
                />
                <DetailRow
                  leadingIcon="close"
                  label={t('insights.byStatus.cancelled')}
                  value={String(summary.byStatus.cancelled)}
                  noDivider
                />
              </RowGroup>
            </CardContent>
          </Card>

          {/* Per-grade kg breakdown */}
          <Card>
            <CardHeader title={t('insights.perGrade.title')} />
            <CardContent>
              <RowGroup>
                <DetailRow
                  leadingIcon="eco"
                  label={t('insights.perGrade.a')}
                  value={t('insights.kgValue', { kg: groupDigits(summary.perGradeKg.A) })}
                />
                <DetailRow
                  leadingIcon="eco"
                  label={t('insights.perGrade.b')}
                  value={t('insights.kgValue', { kg: groupDigits(summary.perGradeKg.B) })}
                />
                <DetailRow
                  leadingIcon="eco"
                  label={t('insights.perGrade.c')}
                  value={t('insights.kgValue', { kg: groupDigits(summary.perGradeKg.C) })}
                  noDivider
                />
              </RowGroup>
            </CardContent>
          </Card>

          {/* Realised earnings — sales count + avg price + total amount */}
          <Card>
            <CardHeader title={t('insights.earnings.title')} />
            <CardContent>
              <RowGroup>
                <DetailRow
                  leadingIcon="fact_check"
                  label={t('insights.earnings.salesCount')}
                  value={String(summary.salesCount)}
                />
                <DetailRow
                  leadingIcon="scale"
                  label={t('insights.earnings.avgPricePerKg')}
                  value={`${summary.saleCurrency} ${groupDigits(summary.avgPricePerKg)} / kg`}
                />
                <DetailRow
                  leadingIcon="payments"
                  label={t('insights.earnings.total')}
                  value={`${summary.saleCurrency} ${groupDigits(summary.totalSalesAmount)}`}
                  noDivider
                />
              </RowGroup>
            </CardContent>
          </Card>
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Source 2 — incentive points balance card. Soft: a spinner while pending, an
// "unavailable" line on error (no blocking alert), the points + a hint that
// links to the Rewards page on success.
// ---------------------------------------------------------------------------
function PointsCard({
  query,
  t,
}: {
  query: UseQueryResult<IncentiveBalance, unknown>;
  t: TFunction;
}) {
  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Text variant="label-medium" color="text.secondary">
              {t('insights.points.label')}
            </Text>
            <Box sx={{ mt: 0.25 }}>
              {query.isPending ? (
                <CircularProgress size={24} />
              ) : query.isError ? (
                <Text variant="title-medium" color="text.secondary">
                  {t('insights.points.unavailable')}
                </Text>
              ) : (
                <Text variant="display-small" color="primary.main" as="span">
                  {t('insights.points.value', { count: query.data?.balance ?? 0 })}
                </Text>
              )}
            </Box>
          </Box>
          {/* Hint → Rewards. Always shown (the rewards page is the home for
              points); a points read failing doesn't hide the route in. */}
          <Link to="/incentives" style={{ textDecoration: 'none' }}>
            <Button
              variant="text"
              endIcon={<Icon name="arrow_forward" size={18} color="inherit" />}
            >
              {t('insights.points.cta')}
            </Button>
          </Link>
        </Box>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Source 3 — earnings rollup card (this calendar month + lifetime). Amounts are
// PLAIN ZAR decimal strings → Number() → <Money>, no 1e6 division. Soft: spinner
// while pending, an "unavailable" line on error.
// ---------------------------------------------------------------------------
function EarningsCard({
  query,
  t,
}: {
  query: UseQueryResult<HarvestEarnings, unknown>;
  t: TFunction;
}) {
  return (
    <Card>
      <CardHeader title={t('insights.earningsRollup.title')} />
      <CardContent>
        {query.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : query.isError || !query.data ? (
          <Text variant="body-medium" color="text.secondary">
            {t('insights.earningsRollup.unavailable')}
          </Text>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <EarningsCell
              label={t('insights.earningsRollup.thisMonth')}
              amount={Number(query.data.thisMonth.salesAmount)}
              salesCount={query.data.thisMonth.salesCount}
              t={t}
            />
            <EarningsCell
              label={t('insights.earningsRollup.lifetime')}
              amount={Number(query.data.lifetime.salesAmount)}
              salesCount={query.data.lifetime.salesCount}
              t={t}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function EarningsCell({
  label,
  amount,
  salesCount,
  t,
}: {
  label: string;
  amount: number;
  salesCount: number;
  t: TFunction;
}) {
  return (
    <Box>
      <Text variant="label-medium" color="text.secondary">
        {label}
      </Text>
      <Box sx={{ mt: 0.25 }}>
        <Money amount={Number.isFinite(amount) ? amount : 0} size="lg" />
      </Box>
      <Box sx={{ mt: 0.25 }}>
        <Text variant="body-small" color="text.secondary">
          {t('insights.earningsRollup.salesCount', { count: salesCount })}
        </Text>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Source 4 — monthly trend chart. Reuses the dependency-free Sparkline +
// MarketStatBar pattern from _app.price-dashboard.tsx: a headline of the latest
// month's kg with a +/- delta vs the previous month, a kg Sparkline across the
// window, and a per-month grid (kg + sales) below. Soft: spinner / "no data".
// ---------------------------------------------------------------------------
function TrendCard({
  query,
  t,
}: {
  query: UseQueryResult<HarvestTrendList, unknown>;
  t: TFunction;
}) {
  const { mode } = useColorMode();
  const sys = mdSysColor[mode];

  const points: HarvestTrendPoint[] = query.data?.data ?? [];

  return (
    <Card>
      <CardHeader title={t('insights.trend.title')} />
      <CardContent>
        {query.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : query.isError || points.length === 0 ? (
          <Text variant="body-medium" color="text.secondary">
            {t('insights.trend.noData')}
          </Text>
        ) : (
          (() => {
            const kgValues = points.map((p) => {
              const n = Number(p.totalKg);
              return Number.isFinite(n) ? n : 0;
            });
            const latestKg = kgValues[kgValues.length - 1] ?? 0;
            const prevKg = kgValues.length >= 2 ? kgValues[kgValues.length - 2]! : null;

            // Month-over-month %-change vs the previous month (null if no prior
            // month, or the prior month was zero so a % is undefined).
            const pct =
              prevKg != null && prevKg > 0 ? ((latestKg - prevKg) / prevKg) * 100 : null;
            const direction: 'up' | 'down' | 'neutral' =
              pct == null || Math.abs(pct) < 0.05 ? 'neutral' : pct > 0 ? 'up' : 'down';
            const deltaLabel =
              pct == null
                ? undefined
                : t('insights.trend.delta', {
                    value: `${pct > 0 ? '+' : ''}${pct.toFixed(0)}`,
                  });
            const sparkColor = direction === 'down' ? sys.error : sys.primary;
            const latestMonth = points[points.length - 1]!.month;

            return (
              <Stack spacing={2}>
                <MarketStatBar
                  icon="scale"
                  label={t('insights.trend.headlineLabel')}
                  value={
                    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t('insights.kgValue', { kg: groupDigits(String(latestKg)) })}
                    </Box>
                  }
                  delta={deltaLabel}
                  deltaDirection={direction}
                  meta={t('insights.trend.headlineMeta', {
                    month: formatMonthShort(latestMonth),
                  })}
                  chart={
                    <Sparkline
                      points={kgValues}
                      width={160}
                      height={44}
                      color={sparkColor}
                      ariaLabel={t('insights.trend.sparkAria', { months: points.length })}
                    />
                  }
                />

                {/* Per-month breakdown — kg opened + settled sales. */}
                <RowGroup>
                  {points.map((p, i) => (
                    <DetailRow
                      key={p.month}
                      leadingIcon="event"
                      label={formatMonthShort(p.month)}
                      value={
                        <Box component="span">
                          {t('insights.kgValue', { kg: groupDigits(p.totalKg) })}
                          {' · '}
                          <Money amount={microToZarp(p.salesAmountMicro)} size="sm" />
                        </Box>
                      }
                      noDivider={i === points.length - 1}
                    />
                  ))}
                </RowGroup>
              </Stack>
            );
          })()
        )}
      </CardContent>
    </Card>
  );
}

export default InsightsScreen;
